/**
 * Anmeldung.
 *
 * Der Microsoft-Knopf erscheint nur, wenn der Server sagt, dass die
 * Verbindung eingerichtet ist. Ein Knopf, der ins Leere führt, ist auf einer
 * Anmeldeseite schlimmer als gar keiner.
 */

import { useEffect, useState, type FormEvent } from 'react'
import { api } from '../api/klient'
import { Fehlerhinweis } from '../bausteine/Hinweise'
import { useKonto, type Zweitschritt } from '../konto/KontoKontext'
import logo from '../assets/spuer-logo.png'
import hintergrund from '../assets/hero.jpg'

export function Anmeldung() {
  const { anmelden, zweitfaktorAbschliessen, abmeldehinweis } = useKonto()
  const [email, setzeEmail] = useState('')
  const [passwort, setzePasswort] = useState('')
  const [laeuft, setzeLaeuft] = useState(false)
  const [fehler, setzeFehler] = useState<unknown>(null)
  const [microsoft, setzeMicrosoft] = useState(false)
  /* Der zweite Schritt. Solange er steht, zeigt die Seite nur ihn – das
     Passwortformular hat seine Arbeit getan, und zwei offene Formulare
     nebeneinander verwirren mehr, als sie helfen. */
  const [zweitschritt, setzeZweitschritt] = useState<Zweitschritt | null>(null)
  const [code, setzeCode] = useState('')
  const [mailLaeuft, setzeMailLaeuft] = useState(false)
  const [mailVerschickt, setzeMailVerschickt] = useState(false)

  useEffect(() => {
    const abbruch = new AbortController()
    api
      .holen<{ verfuegbar: boolean }>('/api/auth/microsoft/verfuegbar', abbruch.signal)
      .then((antwort) => setzeMicrosoft(antwort.verfuegbar))
      // Ohne Verbindung bleibt der Knopf weg. Das Passwortformular geht
      // trotzdem – es meldet sich beim Absenden mit einer klaren Meldung.
      .catch(() => undefined)
    return () => abbruch.abort()
  }, [])

  async function absenden(ereignis: FormEvent) {
    ereignis.preventDefault()
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      // Hat das Konto einen zweiten Faktor, kommt hier ein Anmeldeschritt
      // zurück und noch keine Sitzung.
      const schritt = await anmelden(email.trim(), passwort)
      if (schritt) setzeZweitschritt(schritt)
    } catch (f) {
      setzeFehler(f)
    } finally {
      setzeLaeuft(false)
    }
  }

  async function codeAbsenden(ereignis: FormEvent) {
    ereignis.preventDefault()
    if (!zweitschritt) return
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      await zweitfaktorAbschliessen(zweitschritt.schritt, code.trim())
    } catch (f) {
      setzeFehler(f)
    } finally {
      setzeLaeuft(false)
    }
  }

  async function codePerMail() {
    if (!zweitschritt) return
    setzeFehler(null)
    setzeMailLaeuft(true)
    try {
      await api.senden('/api/auth/anmelden/zweitfaktor/mail', {
        schritt: zweitschritt.schritt,
      })
      setzeMailVerschickt(true)
    } catch (f) {
      setzeFehler(f)
    } finally {
      setzeMailLaeuft(false)
    }
  }

  return (
    <div className="relative min-h-dvh">
      {/* Das Foto liegt hinter einer hellen Schicht: das Formular muss bei
          Sonne auf dem Handy lesbar bleiben, das Bild ist nur Begrüßung. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${hintergrund})` }}
      />
      <div aria-hidden className="absolute inset-0 bg-white/90 backdrop-blur-[3px]" />

      <div className="relative mx-auto flex min-h-dvh max-w-md flex-col justify-center p-4">
        {/* Logo und Formular teilen sich eine weiße Fläche. Getrennt lag das
            Logo mit seinem durchsichtigen Grund direkt auf dem Foto – am
            Rechner ging das noch, auf dem Handy nicht: dort wird das Bild
            durch bg-cover viel stärker vergrößert, und die schmale Unterzeile
            „SPIELPLATZ ÜBERPRÜFEN RULF" stand auf unruhigem Grund. */}
        <div className="karte space-y-5 shadow-sm">
          <header className="text-center">
            <img
              src={logo}
              alt="SPÜR – Spielplatz Überprüfen Rulf"
              width={897}
              height={392}
              className="mx-auto h-auto w-full max-w-[15rem]"
            />
            <p className="mt-3 text-slate-700">Prüfmanager für Spielplatzprüfungen</p>
          </header>

          {abmeldehinweis && (
            <p
              role="status"
              className="rounded-feld border-2 border-stufe-2 bg-stufe-2-grund p-3 text-stufe-2"
            >
              {abmeldehinweis}
            </p>
          )}

          {zweitschritt ? (
            <form onSubmit={codeAbsenden} className="space-y-4">
              <div>
                <h2 className="text-lg font-bold">Zweiter Schritt</h2>
                <p className="mt-1 text-base text-slate-700">
                  {zweitschritt.arten.includes('app')
                    ? 'Geben Sie den sechsstelligen Code aus Ihrer Authenticator-App ein.'
                    : 'Wir schicken Ihnen einen Code per E-Mail.'}
                </p>
              </div>

              <div>
                <label htmlFor="code">Code</label>
                <input
                  id="code"
                  value={code}
                  onChange={(e) => setzeCode(e.target.value)}
                  required
                  autoFocus
                  autoComplete="one-time-code"
                  inputMode="text"
                  spellCheck={false}
                  placeholder="123456"
                />
                <p className="mt-1 text-base text-slate-600">
                  Ein Wiederherstellungscode geht hier ebenso — Sie müssen nicht
                  raten, was wohin gehört.
                </p>
              </div>

              <Fehlerhinweis fehler={fehler} />

              <button
                type="submit"
                className="knopf-haupt w-full"
                disabled={laeuft || !code.trim()}
              >
                {laeuft ? 'Prüft …' : 'Anmelden'}
              </button>

              {zweitschritt.arten.includes('mail') && (
                <button
                  type="button"
                  className="knopf-still w-full"
                  disabled={mailLaeuft || mailVerschickt}
                  onClick={() => void codePerMail()}
                >
                  {mailVerschickt
                    ? 'Code ist unterwegs'
                    : mailLaeuft
                      ? 'Schickt …'
                      : 'Code per E-Mail schicken'}
                </button>
              )}

              <button
                type="button"
                className="knopf-still w-full"
                onClick={() => {
                  setzeZweitschritt(null)
                  setzeCode('')
                  setzeFehler(null)
                  setzeMailVerschickt(false)
                }}
              >
                Abbrechen
              </button>
            </form>
          ) : (
          <form onSubmit={absenden} className="space-y-4">
            <div>
              <label htmlFor="email">E-Mail-Adresse</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setzeEmail(e.target.value)}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
                enterKeyHint="next"
              />
            </div>

            <div>
              <label htmlFor="passwort">Passwort</label>
              <input
                id="passwort"
                type="password"
                value={passwort}
                onChange={(e) => setzePasswort(e.target.value)}
                autoComplete="current-password"
                required
                enterKeyHint="go"
              />
            </div>

            <Fehlerhinweis fehler={fehler} />

            <button type="submit" className="knopf-haupt w-full" disabled={laeuft}>
              {laeuft ? 'Meldet an …' : 'Anmelden'}
            </button>

            {microsoft && (
              <>
                <div className="flex items-center gap-3 text-base text-slate-600">
                  <span className="h-px flex-1 bg-slate-300" />
                  oder
                  <span className="h-px flex-1 bg-slate-300" />
                </div>
                {/* Ein echter Verweis, kein fetch: der Anmeldevorgang verlässt
                    die Seite und kommt über einen Rücksprung zurück. */}
                <a href="/api/auth/microsoft/start" className="knopf-zweit w-full">
                  Mit Microsoft 365 anmelden
                </a>
              </>
            )}
          </form>
          )}
        </div>

        {/* Steht außerhalb der Fläche, aber auf hellem Grund – deshalb ein
            kräftigeres Grau als im Formular. */}
        <p className="mt-4 text-center text-base text-slate-700">
          Konten werden auf Einladung angelegt. Wenden Sie sich an das Prüfbüro.
        </p>
      </div>
    </div>
  )
}
